<template>
  <div class="space-y-6">
    <UiKitInput
      v-model="form.secret_key"
      type="password"
      label="Secret Key"
      placeholder="Введите секретный ключ"
    />

    <UiKitInput
      v-model="channelText"
      label="Channel"
      placeholder="Введите канал"
    />

    <UiKitInput
      v-model="affiliateText"
      label="Affiliate"
      placeholder="Введите партнёра"
    />

    <!-- Order Tags Map -->
    <div>
      <label class="block text-sm font-medium text-hui-foreground/80 mb-1">Карта тегов заказов</label>
      <p class="text-xs text-hui-foreground/60 mb-3">
        ID тегов FrontPad для каждого типа заказа. Оставьте пустым, чтобы не отправлять тег.
      </p>
      <div class="grid grid-cols-[1fr_120px] gap-2 items-center">
        <template v-for="item in orderTagsCatalog" :key="item.key">
          <span class="text-sm text-hui-foreground">{{ item.label }}</span>
          <UiKitInput
            v-model.number="orderTagsInputs[item.key]"
            type="number"
            placeholder="код"
          />
        </template>
      </div>
    </div>

    <!-- Hook Statuses -->
    <div>
      <label class="block text-sm font-medium text-hui-foreground/80 mb-1">Подписка на статусы FrontPad</label>
      <p class="text-xs text-hui-foreground/60 mb-3">
        Выберите статусы, по которым FrontPad будет вызывать webhook.
        Если ничего не выбрано — подпишемся на все релевантные для типа заказа.
      </p>
      <div class="grid grid-cols-2 gap-2">
        <label
          v-for="item in hookStatusesCatalog"
          :key="item.key"
          class="flex items-center gap-2 text-sm cursor-pointer"
        >
          <UiKitCheckbox v-model="hookStatusesChecked[item.key]" />
          <span class="text-hui-foreground">{{ item.label }}</span>
          <span class="text-hui-foreground/40">(код {{ item.code }})</span>
        </label>
      </div>
    </div>

    <!-- Initial Status Map -->
    <div>
      <label class="block text-sm font-medium text-hui-foreground/80 mb-1">Начальный статус при создании заказа</label>
      <p class="text-xs text-hui-foreground/60 mb-3">
        Какой статус FrontPad проставит сразу при создании. Сейчас используется
        только «Предзаказ» — для всех заказов с отложенной датой. Пустой код =
        берётся значение по умолчанию из каталога.
      </p>
      <div class="grid grid-cols-[1fr_120px_auto] gap-2 items-center">
        <template v-for="item in initialStatusesCatalog" :key="item.key">
          <span class="text-sm text-hui-foreground">{{ item.label }}</span>
          <UiKitInput
            v-model.number="initialStatusInputs[item.key]"
            type="number"
            placeholder="код"
          />
          <span v-if="item.is_default" class="text-xs text-hui-foreground/40">по умолчанию</span>
          <span v-else></span>
        </template>
      </div>
    </div>

    <!-- Payment Methods Map -->
    <div>
      <label class="block text-sm font-medium text-hui-foreground/80 mb-1">Карта способов оплаты (JSON)</label>
      <UiKitTextarea
        v-model="paymentMethodsMapText"
        placeholder='Например: { "default": 2, "cash": 1, "card": 2 }'
        :rows="4"
      />
    </div>

    <div class="flex gap-3 pt-4 border-t border-hui-divider">
      <UiKitButton type="button" color="primary" :loading="loading" :disabled="loading" @click="saveSettings">
        Сохранить настройки
      </UiKitButton>
      <UiKitButton type="button" color="default" variant="flat" @click="closeModal">
        Отмена
      </UiKitButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import consola from 'consola'
import { useKitToast } from '@shared/ui/kit'
import { APIAdminInstitution } from '~/fsd-pages/admin/api'
import { useModalStore } from '@shared/model'
import type {
  FrontPadSettings,
  FrontPadCatalog,
  FrontPadHookStatusKey,
  FrontPadOrderTagKey,
  HookStatusCatalogItem,
  OrderTagCatalogItem,
  InitialStatusCatalogItem,
} from '@entities/institution'

const emit = defineEmits<{
  'update:size': [size: string]
}>()

const { toast: kitToast } = useKitToast()
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    kitToast({ color: o.color === 'green' ? 'success' : o.color === 'red' ? 'danger' : o.color === 'orange' ? 'warning' : 'default', title: o.title, description: o.description, timeout: o.timeout }),
}

const modalStore = useModalStore()
const loading = ref(false)

const form = ref<FrontPadSettings>({
  secret_key: '',
  channel: null,
  affiliate: null,
  payment_methods_map: {},
  order_tags_map: {},
  hook_statuses_map: {},
  initial_status_map: {},
})

const hookStatusesCatalog = ref<HookStatusCatalogItem[]>([])
const orderTagsCatalog = ref<OrderTagCatalogItem[]>([])
const initialStatusesCatalog = ref<InitialStatusCatalogItem[]>([])

const hookStatusesChecked = reactive<Record<string, boolean>>({})
const orderTagsInputs = reactive<Record<string, number | null>>({})
const initialStatusInputs = reactive<Record<string, number | null>>({})

const channelText = computed<string>({
  get: () => (form.value.channel === null || form.value.channel === undefined ? '' : String(form.value.channel)),
  set: (v) => {
    form.value.channel = v === '' ? null : v
  },
})

const affiliateText = computed<string>({
  get: () => {
    const a = form.value.affiliate
    if (a === null || a === undefined) return ''
    return typeof a === 'string' ? a : JSON.stringify(a)
  },
  set: (v) => {
    form.value.affiliate = v === '' ? null : v
  },
})

const paymentMethodsMapText = computed<string>({
  get: () => JSON.stringify(form.value.payment_methods_map || {}, null, 2),
  set: (value: string) => {
    try {
      form.value.payment_methods_map = JSON.parse(value)
    } catch {
      // оставляем невалидный JSON в textarea — пользователь поправит
    }
  },
})

function applyCatalogToForm(catalog: FrontPadCatalog) {
  hookStatusesCatalog.value = catalog.hook_statuses
  orderTagsCatalog.value = catalog.order_tags
  initialStatusesCatalog.value = catalog.initial_statuses

  for (const item of catalog.hook_statuses) {
    hookStatusesChecked[item.key] = item.enabled
  }
  for (const item of catalog.order_tags) {
    orderTagsInputs[item.key] = item.code
  }
  for (const item of catalog.initial_statuses) {
    initialStatusInputs[item.key] = item.is_default ? null : item.code
  }
}

function collectMapsFromInputs() {
  const hookMap: Partial<Record<FrontPadHookStatusKey, boolean>> = {}
  for (const item of hookStatusesCatalog.value) {
    if (hookStatusesChecked[item.key]) {
      hookMap[item.key] = true
    }
  }
  form.value.hook_statuses_map = hookMap

  const tagsMap: Partial<Record<FrontPadOrderTagKey, number>> = {}
  for (const item of orderTagsCatalog.value) {
    const raw = orderTagsInputs[item.key]
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      tagsMap[item.key] = raw
    }
  }
  form.value.order_tags_map = tagsMap

  const initialMap: Partial<Record<FrontPadOrderTagKey, number>> = {}
  for (const item of initialStatusesCatalog.value) {
    const raw = initialStatusInputs[item.key]
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      initialMap[item.key] = raw
    }
  }
  form.value.initial_status_map = initialMap
}

const loadSettings = async () => {
  try {
    loading.value = true
    const response = await APIAdminInstitution.getFrontPadSettings()

    const settings = response.frontpad || ({} as FrontPadSettings)
    form.value = {
      secret_key: settings.secret_key || '',
      channel: settings.channel ?? null,
      affiliate: settings.affiliate ?? null,
      payment_methods_map: settings.payment_methods_map || {},
      order_tags_map: settings.order_tags_map || {},
      hook_statuses_map: settings.hook_statuses_map || {},
      initial_status_map: settings.initial_status_map || {},
    }

    applyCatalogToForm(response.catalog)
  } catch (error) {
    consola.error('Ошибка загрузки настроек FrontPad:', error)
    toast.add({ title: 'Ошибка', description: 'Ошибка загрузки настроек', color: 'red' })
  } finally {
    loading.value = false
  }
}

const saveSettings = async () => {
  try {
    loading.value = true

    try {
      JSON.parse(paymentMethodsMapText.value)
    } catch {
      toast.add({ title: 'Ошибка JSON', description: 'Ошибка JSON в "Карта способов оплаты"', color: 'red' })
      return
    }

    collectMapsFromInputs()
    await APIAdminInstitution.updateFrontPadSettings(form.value)
    toast.add({ title: 'Сохранено', description: 'Настройки FrontPad успешно сохранены', color: 'green' })

    setTimeout(() => {
      closeModal()
    }, 1500)
  } catch (error) {
    consola.error('Ошибка сохранения настроек FrontPad:', error)
    toast.add({ title: 'Ошибка', description: 'Ошибка сохранения настроек', color: 'red' })
  } finally {
    loading.value = false
  }
}

const closeModal = () => {
  modalStore.close()
}

onMounted(() => {
  emit('update:size', 'l')
  loadSettings()
})
</script>
